// assets/custom-newsletter-popup.js
(function () {
  'use strict';

  const POPUP_SEL = '#newsletter-popup';

  // ── Popup ──────────────────────────────────────────────────────────────────

  class NewsletterPopup {
    constructor(el) {
      this.el          = el;
      this.overlay     = el.querySelector('[data-nlp-overlay]');
      this.closeBtn    = el.querySelector('[data-nlp-close]');
      this.trigger     = el.dataset.trigger     || 'delay';
      this.delay       = (parseInt(el.dataset.delay) || 5) * 1000;
      this.scrollDepth = parseInt(el.dataset.scrollDepth) || 50;
      this.showOnce    = el.dataset.showOnce    || 'session';
      this.storageKey  = el.dataset.storageKey  || 'mfb_newsletter';
      this.pageRule    = el.dataset.pageRestriction || 'all-pages';
      this.closeOnOverlay = el.dataset.closeOnOverlay !== 'false';
      this._prevFocus  = null;
      this._scrollHandler = null;
      this._exitHandler   = null;
      this._timer         = null;

      if (!this._shouldShow()) return;
      this._setup();
    }

    _shouldShow() {
      if (!this._matchesPageRule()) return false;
      const stored = this._getStorage();
      if (!stored) return true;
      if (this.showOnce === 'never-again') return false;
      if (this.showOnce === 'session')     return false; // sessionStorage already set
      const now = Date.now();
      if (this.showOnce === 'day'  && now - stored < 86400000)   return false;
      if (this.showOnce === 'week' && now - stored < 604800000)  return false;
      return true;
    }

    _matchesPageRule() {
      const path = window.location.pathname;
      if (this.pageRule === 'homepage-only') return path === '/';
      if (this.pageRule === 'not-cart')      return !path.startsWith('/cart');
      return true;
    }

    _getStorage() {
      try {
        if (this.showOnce === 'session') return sessionStorage.getItem(this.storageKey);
        return localStorage.getItem(this.storageKey);
      } catch (_) { return null; }
    }

    _setStorage() {
      try {
        const val = this.showOnce === 'never-again' ? '1' : String(Date.now());
        if (this.showOnce === 'session') sessionStorage.setItem(this.storageKey, val);
        else                             localStorage.setItem(this.storageKey, val);
      } catch (_) {}
    }

    _setup() {
      if (this.trigger === 'delay' || this.trigger === 'both') {
        this._timer = setTimeout(() => this.show(), this.delay);
      }
      if (this.trigger === 'exit-intent' || this.trigger === 'both') {
        this._exitHandler = e => {
          if (e.clientY <= 0) { this.show(); document.removeEventListener('mouseleave', this._exitHandler); }
        };
        document.addEventListener('mouseleave', this._exitHandler);
      }
      if (this.trigger === 'scroll-depth') {
        this._scrollHandler = () => {
          const scrolled = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
          if (scrolled >= this.scrollDepth) { this.show(); window.removeEventListener('scroll', this._scrollHandler); }
        };
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
      }

      this.closeBtn?.addEventListener('click', () => this.hide());
      if (this.closeOnOverlay) this.overlay?.addEventListener('click', () => this.hide());
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && !this.el.classList.contains('is-open')) return; if (e.key === 'Escape') this.hide(); });
    }

    show() {
      clearTimeout(this._timer);
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      this._prevFocus = document.activeElement;
      this._trapFocus();
      document.body.style.overflow = 'hidden';
    }

    hide() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._prevFocus?.focus();
      this._setStorage();
    }

    _trapFocus() {
      const focusable = this.el.querySelectorAll('a[href],button:not([disabled]),input,textarea,select,[tabindex]:not([tabindex="-1"])');
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (first) setTimeout(() => first.focus(), 50);
      this.el.addEventListener('keydown', e => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
        else            { if (document.activeElement === last)  { e.preventDefault(); first.focus(); } }
      });
    }

    destroy() {
      clearTimeout(this._timer);
      if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
      if (this._exitHandler)   document.removeEventListener('mouseleave', this._exitHandler);
    }
  }

  // ── Form submission ─────────────────────────────────────────────────────────

  async function submitForm(form) {
    const platform = form.dataset.platform || 'shopify';
    const email    = form.querySelector('[type="email"]')?.value?.trim();
    const content  = form.closest('[data-nl-content]');
    const errorEl  = content?.querySelector('.hbk-nl__error');
    const submitBtn = form.querySelector('[type="submit"]');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError(content, errorEl, 'Please enter a valid email address.');
      return;
    }

    submitBtn?.setAttribute('aria-busy', 'true');
    submitBtn && (submitBtn.disabled = true);
    clearError(content);

    try {
      if (platform === 'klaviyo') {
        await submitKlaviyo(email, form.dataset.klaviyoList);
      } else if (platform === 'mailchimp') {
        await submitMailchimp(email, form.dataset.mailchimpUrl);
      } else {
        await submitShopify(form);
      }
      showSuccess(content);
    } catch (err) {
      showError(content, errorEl, err.message || 'Something went wrong. Please try again.');
    } finally {
      submitBtn?.removeAttribute('aria-busy');
      submitBtn && (submitBtn.disabled = false);
    }
  }

  async function submitShopify(form) {
    const data = new URLSearchParams(new FormData(form));
    const res  = await fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    });
    if (!res.ok && res.status !== 302) throw new Error('Subscription failed. Please try again.');
  }

  async function submitKlaviyo(email, listId) {
    if (!listId) throw new Error('Klaviyo list ID not configured.');
    const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(listId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2023-02-22' },
      body: JSON.stringify({ data: { type: 'subscription', attributes: { list_id: listId, email } } }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.errors?.[0]?.detail || 'Klaviyo subscription failed.');
    }
  }

  async function submitMailchimp(email, actionUrl) {
    if (!actionUrl) throw new Error('Mailchimp action URL not configured.');
    const jsonUrl = actionUrl.replace('/post?', '/post-json?') + '&c=?';
    const url     = `${jsonUrl}&EMAIL=${encodeURIComponent(email)}`;
    return new Promise((resolve, reject) => {
      const cbName = 'mc_' + Date.now();
      const script = document.createElement('script');
      window[cbName] = data => {
        delete window[cbName];
        script.remove();
        data.result === 'success' ? resolve() : reject(new Error(data.msg?.replace(/<[^>]+>/g, '') || 'Mailchimp error.'));
      };
      script.src = url + '&c=' + cbName;
      document.head.appendChild(script);
    });
  }

  function showSuccess(content) {
    content?.classList.remove('has-error');
    content?.classList.add('is-success');
  }

  function showError(content, errorEl, msg) {
    content?.classList.add('has-error');
    if (errorEl) errorEl.textContent = msg;
  }

  function clearError(content) {
    content?.classList.remove('has-error');
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  let popupInstance = null;

  function initPopup() {
    const el = document.querySelector(POPUP_SEL);
    if (el && !popupInstance) popupInstance = new NewsletterPopup(el);
  }

  function initForms(scope = document) {
    scope.querySelectorAll('[data-newsletter-form]').forEach(form => {
      if (form.dataset.nlBound) return;
      form.dataset.nlBound = '1';
      form.addEventListener('submit', e => {
        e.preventDefault();
        submitForm(form);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initPopup();
    initForms();
  });

  document.addEventListener('shopify:section:load', e => initForms(e.target));

})();
